import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Circle,
  Clock,
  Plus,
  AlertTriangle,
  Building,
  Calendar,
  Loader2,
  Trash2,
  Phone,
  ChevronRight,
  ListTodo,
  X,
  Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_STYLES = {
  high: 'bg-red-500/10 text-red-600 border-red-500/20',
  medium: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  low: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
};

const STATUS_ICONS = {
  todo: <Circle className="w-4 h-4 text-muted-foreground" />,
  in_progress: <Clock className="w-4 h-4 text-amber-600" />,
  done: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
};

function TaskRow({ task, onToggle, onDelete, onEdit }) {
  const navigate = useNavigate();
  const isOverdue = task.status !== 'done' && task.due_date && new Date(task.due_date) < new Date();
  const dueStr = task.due_date ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all hover:border-primary/20 ${
        task.status === 'done' ? 'bg-secondary/30 border-border/30' : 'bg-card border-border/50'
      } ${isOverdue ? 'border-red-500/30' : ''}`}
      data-testid={`task-row-${task.task_id}`}
    >
      {/* Checkbox */}
      <button
        onClick={() => onToggle(task)}
        className="shrink-0"
        data-testid={`task-toggle-${task.task_id}`}
      >
        {STATUS_ICONS[task.status]}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onEdit(task)}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium ${task.status === 'done' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
            {task.title}
          </span>
          <Badge className={`text-[10px] px-1.5 py-0 border ${PRIORITY_STYLES[task.priority]}`}>
            {task.priority}
          </Badge>
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
          {task.company && (
            <span className="flex items-center gap-1">
              <Building className="w-3 h-3" />
              {task.company}
            </span>
          )}
          {dueStr && (
            <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
              <Calendar className="w-3 h-3" />
              {dueStr}
              {isOverdue && ' (overdue)'}
            </span>
          )}
          {task.call_id && (
            <button
              className="flex items-center gap-1 hover:text-primary"
              onClick={(e) => { e.stopPropagation(); navigate(`/report/${task.call_id}`); }}
            >
              <Phone className="w-3 h-3" />
              View call
            </button>
          )}
          {task.assigned_name && task.assigned_name !== 'Unknown' && (
            <span>{task.assigned_name}</span>
          )}
        </div>
      </div>

      {/* Delete */}
      <button
        onClick={() => onDelete(task.task_id)}
        className="shrink-0 p-1 text-muted-foreground/40 hover:text-red-600 transition-colors"
        data-testid={`task-delete-${task.task_id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function DealCombobox({ deals, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keep the input display in sync when value changes externally or dropdown closes
  useEffect(() => {
    if (!open) setQuery(value || '');
  }, [value, open]);

  const filtered = query.trim()
    ? deals.filter(d => d.company.toLowerCase().includes(query.toLowerCase()))
    : deals;

  const handleSelect = (company) => {
    onChange(company);
    setQuery(company);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder="Search deals..."
          className="bg-background border-border pl-9 pr-9"
          data-testid="task-deal-combobox-input"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-secondary"
            data-testid="task-deal-combobox-clear"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )}
      </div>
      {open && (
        <div
          className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
          data-testid="task-deal-combobox-list"
        >
          <button
            type="button"
            onClick={() => handleSelect('')}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center justify-between ${!value ? 'bg-secondary/60' : ''}`}
            data-testid="task-deal-option-none"
          >
            <span className="text-muted-foreground">No deal</span>
            {!value && <Check className="w-3.5 h-3.5 text-primary" />}
          </button>
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground text-center">
              No deals match "{query}"
            </div>
          ) : (
            filtered.map(d => (
              <button
                type="button"
                key={d.company}
                onClick={() => handleSelect(d.company)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center justify-between ${value === d.company ? 'bg-secondary/60' : ''}`}
                data-testid={`task-deal-option-${d.company}`}
              >
                <span className="truncate">{d.company}</span>
                {value === d.company && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}


export default function Tasks() {
  const { user, isManager } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [showCreate, setShowCreate] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dealsList, setDealsList] = useState([]);

  // Form state
  const [form, setForm] = useState({ title: '', due_date: '', company: '', priority: 'medium', notes: '', assigned_to: '' });

  useEffect(() => {
    fetchTasks();
    fetchTemplates();
    fetchSummary();
    fetchDeals();
  }, [filter]);

  const fetchTasks = async () => {
    try {
      const statusParam = filter === 'active' ? 'all' : filter;
      const res = await fetch(`${API_URL}/api/tasks?status=${statusParam}`, { credentials: 'include' });
      if (res.ok) {
        let data = await res.json();
        if (filter === 'active') data = data.filter(t => t.status !== 'done');
        setTasks(data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/templates`, { credentials: 'include' });
      if (res.ok) setTemplates(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/summary`, { credentials: 'include' });
      if (res.ok) setSummary(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchDeals = async () => {
    try {
      const res = await fetch(`${API_URL}/api/dashboard/deals`, { credentials: 'include' });
      if (res.ok) setDealsList(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Task title is required'); return; }
    try {
      const res = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success('Task created');
        setShowCreate(false);
        setForm({ title: '', due_date: '', company: '', priority: 'medium', notes: '', assigned_to: '' });
        fetchTasks();
        fetchSummary();
      }
    } catch (e) { toast.error('Failed to create task'); }
  };

  const handleToggle = async (task) => {
    const nextStatus = task.status === 'done' ? 'todo' : task.status === 'todo' ? 'in_progress' : 'done';
    try {
      const res = await fetch(`${API_URL}/api/tasks/${task.task_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: nextStatus })
      });
      if (res.ok) { fetchTasks(); fetchSummary(); }
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (taskId) => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/${taskId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Task deleted'); fetchTasks(); fetchSummary(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleUpdate = async () => {
    if (!editTask) return;
    try {
      const res = await fetch(`${API_URL}/api/tasks/${editTask.task_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: editTask.title,
          due_date: editTask.due_date,
          priority: editTask.priority,
          notes: editTask.notes,
          status: editTask.status,
        })
      });
      if (res.ok) { toast.success('Task updated'); setEditTask(null); fetchTasks(); fetchSummary(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleTemplateClick = (tpl) => {
    setForm({ ...form, title: tpl.title, priority: tpl.priority });
    setShowCreate(true);
  };

  const now = new Date();
  const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const weekEnd = new Date(now.getTime() + 7 * 86400000);

  const today = tasks.filter(t => t.status !== 'done' && t.due_date && new Date(t.due_date) < todayEnd);
  const upcoming = tasks.filter(t => t.status !== 'done' && t.due_date && new Date(t.due_date) >= todayEnd && new Date(t.due_date) < weekEnd);
  const later = tasks.filter(t => t.status !== 'done' && (!t.due_date || new Date(t.due_date) >= weekEnd));
  const completed = tasks.filter(t => t.status === 'done');

  return (
    <Layout>
      <div className="space-y-5" data-testid="tasks-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Tasks</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Manage follow-ups and next steps</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-28 h-8 text-xs bg-card border-border" data-testid="task-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="todo">To Do</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="done">Completed</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-8 text-xs"
              data-testid="create-task-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Task
            </Button>
          </div>
        </div>

        {/* Summary counters */}
        {summary && (
          <div className="flex gap-3 text-sm" data-testid="task-summary">
            <span className="px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">
              {summary.todo} to do
            </span>
            <span className="px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-600">
              {summary.in_progress} in progress
            </span>
            <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600">
              {summary.done} done
            </span>
            {summary.overdue > 0 && (
              <span className="px-2.5 py-1 rounded-full bg-red-500/10 text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {summary.overdue} overdue
              </span>
            )}
          </div>
        )}

        {/* Quick Templates */}
        <div className="flex gap-1.5 flex-wrap">
          {templates.map(tpl => (
            <button
              key={tpl.key}
              onClick={() => handleTemplateClick(tpl)}
              className="px-2.5 py-1 rounded-full text-[11px] text-muted-foreground border border-border/50 hover:bg-primary/5 hover:text-primary hover:border-primary/30 transition-colors"
              data-testid={`template-${tpl.key}`}
            >
              + {tpl.title}
            </button>
          ))}
        </div>

        {/* Task Lists */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : tasks.length === 0 ? (
          <Card className="bg-card border-border/50">
            <CardContent className="p-12 text-center">
              <ListTodo className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">No tasks yet</h3>
              <p className="text-muted-foreground mb-4">Create tasks to track follow-ups and next steps</p>
              <Button onClick={() => setShowCreate(true)} className="bg-primary text-primary-foreground">
                <Plus className="w-4 h-4 mr-2" />Create Task
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-5">
            {/* Today */}
            {today.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Today</h3>
                <div className="space-y-1.5">
                  {today.map(t => <TaskRow key={t.task_id} task={t} onToggle={handleToggle} onDelete={handleDelete} onEdit={setEditTask} />)}
                </div>
              </div>
            )}

            {/* Upcoming */}
            {upcoming.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">This Week</h3>
                <div className="space-y-1.5">
                  {upcoming.map(t => <TaskRow key={t.task_id} task={t} onToggle={handleToggle} onDelete={handleDelete} onEdit={setEditTask} />)}
                </div>
              </div>
            )}

            {/* Later */}
            {later.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Later</h3>
                <div className="space-y-1.5">
                  {later.map(t => <TaskRow key={t.task_id} task={t} onToggle={handleToggle} onDelete={handleDelete} onEdit={setEditTask} />)}
                </div>
              </div>
            )}

            {/* Completed */}
            {filter !== 'active' && completed.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Completed</h3>
                <div className="space-y-1.5">
                  {completed.map(t => <TaskRow key={t.task_id} task={t} onToggle={handleToggle} onDelete={handleDelete} onEdit={setEditTask} />)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Task Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-card border-border sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">New Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Title</Label>
                <Input
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="What needs to be done?"
                  className="bg-background border-border"
                  data-testid="task-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Due Date</Label>
                  <Input
                    type="date"
                    value={form.due_date ? form.due_date.slice(0, 10) : ''}
                    onChange={e => setForm({ ...form, due_date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                    className="bg-background border-border"
                    data-testid="task-due-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="bg-background border-border" data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Linked Deal</Label>
                <DealCombobox
                  deals={dealsList}
                  value={form.company}
                  onChange={v => setForm({ ...form, company: v })}
                />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional notes..."
                  className="bg-background border-border"
                />
              </div>
              <Button onClick={handleCreate} className="w-full bg-primary text-primary-foreground" data-testid="task-submit-btn">
                Create Task
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Task Dialog */}
        <Dialog open={!!editTask} onOpenChange={() => setEditTask(null)}>
          <DialogContent className="bg-card border-border sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Task</DialogTitle>
            </DialogHeader>
            {editTask && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={editTask.title}
                    onChange={e => setEditTask({ ...editTask, title: e.target.value })}
                    className="bg-background border-border"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Due Date</Label>
                    <Input
                      type="date"
                      value={editTask.due_date ? editTask.due_date.slice(0, 10) : ''}
                      onChange={e => setEditTask({ ...editTask, due_date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                      className="bg-background border-border"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Priority</Label>
                    <Select value={editTask.priority} onValueChange={v => setEditTask({ ...editTask, priority: v })}>
                      <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={editTask.status} onValueChange={v => setEditTask({ ...editTask, status: v })}>
                    <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">To Do</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Input
                    value={editTask.notes || ''}
                    onChange={e => setEditTask({ ...editTask, notes: e.target.value })}
                    className="bg-background border-border"
                  />
                </div>
                <Button onClick={handleUpdate} className="w-full bg-primary text-primary-foreground">
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
